const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * Thin fetch wrapper.
 *
 * `credentials: 'include'` is mandatory: the session is an httpOnly cookie on
 * a sibling subdomain (rvapi.parktle.com serving rv.parktle.com), so without
 * it the browser sends the request unauthenticated and every call 401s.
 */
/**
 * Called when the API says a request was unauthenticated.
 *
 * Sessions expire, and staff sessions expire in eight idle hours by design. A
 * 401 arriving mid-task is therefore normal, not exceptional — and without a
 * single place to notice it, each screen surfaces its own confusing error
 * ("Couldn't load the worklist") for what is really "you were signed out".
 */
let onUnauthenticated = null;

export function setUnauthenticatedHandler(handler) {
  onUnauthenticated = handler;
}

/** Endpoints where a 401 is the expected answer, not a lost session. */
const EXPECTED_401 = ['/auth/me', '/auth/login', '/auth/register'];

async function request(path, { method = 'GET', body, signal, params } = {}) {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url, {
    method,
    signal,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 204) return null;

  /**
   * A body that will not parse is a configuration fault, not empty data.
   *
   * The case that actually happened: a build without VITE_API_BASE_URL calls
   * `/api/...` relative, which lands on the website's own SPA fallback and
   * returns 200 with index.html. Returning null here meant the caller did
   * `payload.results` on null and the user saw "Cannot read properties of null
   * (reading 'results')" — a message that points at nothing. Say what is
   * actually wrong.
   */
  const contentType = response.headers.get('content-type') ?? '';
  let payload = null;
  if (contentType.includes('json')) {
    payload = await response.json().catch(() => null);
  } else if (response.ok) {
    throw Object.assign(
      new Error(
        `The API returned ${contentType || 'an unknown content type'} instead of JSON. `
        + 'This usually means requests are going to the website rather than the API — '
        + 'check VITE_API_BASE_URL in the build.',
      ),
      { code: 'NOT_JSON', status: response.status },
    );
  }

  if (!response.ok) {
    const error = new Error(payload?.error?.message ?? `Request failed (${response.status})`);
    error.code = payload?.error?.code ?? 'NETWORK_ERROR';
    error.status = response.status;
    error.details = payload?.error?.details ?? null;

    // Announce a lost session once, centrally. /auth/me answering 401 is how we
    // ask "is anyone signed in", so it must not be mistaken for an expiry.
    if (response.status === 401 && !EXPECTED_401.some((p) => path.startsWith(p))) {
      onUnauthenticated?.(error);
    }

    throw error;
  }

  return payload;
}

// --- auth ------------------------------------------------------------------
export const login = (email, password) =>
  request('/auth/login', { method: 'POST', body: { email, password } });

export const register = (payload) =>
  request('/auth/register', { method: 'POST', body: payload });

export const logout = () => request('/auth/logout', { method: 'POST' });

export const logoutEverywhere = () => request('/auth/logout-everywhere', { method: 'POST' });

export const me = () => request('/auth/me');

export const listSessions = () => request('/auth/sessions');

export const forgotPassword = (email) =>
  request('/auth/forgot-password', { method: 'POST', body: { email } });

export const resetPassword = (token, password) =>
  request('/auth/reset-password', { method: 'POST', body: { token, password } });

// --- facilities --------------------------------------------------------------
export const searchFacilities = (params) => request('/facilities/search', { params });

export const getFacility = (slug, vehicleParams) =>
  request(`/facilities/${slug}`, { params: vehicleParams });

export const quoteMoveIn = (body) =>
  request('/facilities/quote', { method: 'POST', body });

// --- vehicles ----------------------------------------------------------------
export const listVehicles = () => request('/vehicles');

export const createVehicle = (body) => request('/vehicles', { method: 'POST', body });

export const updateVehicle = (id, body) => request(`/vehicles/${id}`, { method: 'PATCH', body });

export const deleteVehicle = (id) => request(`/vehicles/${id}`, { method: 'DELETE' });

// --- leases ------------------------------------------------------------------
export const reserveUnit = (body) => request('/leases', { method: 'POST', body });

export const listLeases = () => request('/leases');
export const listMyInvoices = (limit) => request('/leases/invoices', { params: { limit } });

/** The current rotating gate code for one lease. Derived per request. */
export const leaseAccessCode = (leaseId) => request(`/leases/${leaseId}/access-code`);

// --- payments ------------------------------------------------------------------
export const paymentConfig = () => request('/payments/config');

export const startCheckout = (invoiceId) =>
  request(`/payments/invoices/${invoiceId}/checkout`, { method: 'POST' });

export const listInvoices = (limit) => request('/leases/invoices', { params: { limit } });

// --- attendant ---------------------------------------------------------------
const at = (facilityId, path = '') => `/attendant/facilities/${facilityId}${path}`;

export const myFacilities = () => request('/attendant/facilities');

export const verifyGate = (facilityId, body) =>
  request(at(facilityId, '/gate/verify'), { method: 'POST', body });

export const gateLog = (facilityId, limit) =>
  request(at(facilityId, '/gate/log'), { params: { limit } });

export const listArrivals = (facilityId) => request(at(facilityId, '/arrivals'));

export const moveIn = (facilityId, leaseId) =>
  request(at(facilityId, `/leases/${leaseId}/move-in`), { method: 'POST' });

export const moveOut = (facilityId, leaseId, note) =>
  request(at(facilityId, `/leases/${leaseId}/move-out`), { method: 'POST', body: { note } });

export const listAudits = (facilityId) => request(at(facilityId, '/audits'));

export const startAudit = (facilityId) => request(at(facilityId, '/audits'), { method: 'POST' });

export const getAudit = (facilityId, auditId) => request(at(facilityId, `/audits/${auditId}`));

export const recordAuditItem = (facilityId, auditId, body) =>
  request(at(facilityId, `/audits/${auditId}/items`), { method: 'POST', body });

export const completeAudit = (facilityId, auditId, note) =>
  request(at(facilityId, `/audits/${auditId}/complete`), { method: 'POST', body: { note } });

export const listTickets = (facilityId, openOnly = true) =>
  request(at(facilityId, '/tickets'), { params: { open_only: openOnly } });

export const createTicket = (facilityId, body) =>
  request(at(facilityId, '/tickets'), { method: 'POST', body });

// --- admin -------------------------------------------------------------------
export const adminOverview = () => request('/admin/overview');

export const adminDelinquency = (limit) => request('/admin/delinquency', { params: { limit } });

export const adminAuditLog = (limit) => request('/admin/audit-log', { params: { limit } });

export const adminReports = (months) => request('/admin/reports', { params: { months } });

export const adminReportDatasets = () => request('/admin/reports/datasets');

/**
 * A CSV export. Deliberately not routed through `request`, which requires a
 * JSON body and would reject the file.
 *
 * The download is fetched rather than pointed at with a plain link because the
 * session is an httpOnly cookie on another subdomain: a link would send the
 * request without `credentials: 'include'` and the API would answer 401 by
 * navigating the tab away from the console. Fetching keeps the failure inside
 * the page, where it can be shown next to the button that caused it.
 */
export async function downloadReport(dataset, { from, to } = {}) {
  const url = new URL(`${BASE_URL}/admin/reports/export`, window.location.origin);
  url.searchParams.set('dataset', dataset);
  if (from) url.searchParams.set('from', from);
  if (to) url.searchParams.set('to', to);

  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.error?.message ?? `The export failed (${response.status}).`);
  }

  // The filename the server chose carries the date window. Falling back to the
  // dataset name alone would put three different windows in one folder under
  // one name, and the third download would be "invoices (2).csv".
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `${dataset}.csv`;

  return {
    blob: await response.blob(),
    filename,
    rows: Number(response.headers.get('x-row-count') ?? 0),
  };
}

// --- gate maintenance ---------------------------------------------------------
export const updateTicket = (facilityId, ticketId, patch) =>
  request(`/attendant/facilities/${facilityId}/tickets/${ticketId}`, { method: 'PATCH', body: patch });

export const deleteTicket = (facilityId, ticketId) =>
  request(`/attendant/facilities/${facilityId}/tickets/${ticketId}`, { method: 'DELETE' });

export const facilityStaff = (facilityId) =>
  request(`/attendant/facilities/${facilityId}/staff`);

export const gateCredentials = (facilityId) =>
  request(`/attendant/facilities/${facilityId}/credentials`);

export const reissueCredential = (facilityId, leaseId) =>
  request(`/attendant/facilities/${facilityId}/leases/${leaseId}/credentials/reissue`, { method: 'POST' });


export const adminCreateFacility = (body) =>
  request('/admin/facilities', { method: 'POST', body });

export const adminFacility = (facilityId) => request(`/admin/facilities/${facilityId}`);

export const adminUpdateFacility = (facilityId, body) =>
  request(`/admin/facilities/${facilityId}`, { method: 'PATCH', body });

export const adminPreviewUnits = (body) =>
  request('/admin/units/preview', { method: 'POST', body });

export const adminCreateUnits = (facilityId, body) =>
  request(`/admin/facilities/${facilityId}/units`, { method: 'POST', body });

export const adminUnits = (facilityId) => request(`/admin/facilities/${facilityId}/units`);

export const adminUpdateUnit = (facilityId, unitId, body) =>
  request(`/admin/facilities/${facilityId}/units/${unitId}`, { method: 'PATCH', body });

export const adminSuspendAccess = (leaseId, reason) =>
  request(`/admin/leases/${leaseId}/suspend-access`, { method: 'POST', body: { reason } });

export const adminRestoreAccess = (leaseId) =>
  request(`/admin/leases/${leaseId}/restore-access`, { method: 'POST' });

export const adminCreateStaff = (body) =>
  request('/admin/staff', { method: 'POST', body });

export const adminUpdateUser = (userId, body) =>
  request(`/admin/staff/${userId}`, { method: 'PATCH', body });

export const adminSetUserRole = (userId, role) =>
  request(`/admin/staff/${userId}/role`, { method: 'PATCH', body: { role } });

export const adminResetUserPassword = (userId) =>
  request(`/admin/staff/${userId}/reset-password`, { method: 'POST' });

export const adminTenants = (params) => request('/admin/tenants', { params });

export const adminTenant = (userId) => request(`/admin/tenants/${userId}`);

export const adminTenantInvoices = (userId) => request(`/admin/tenants/${userId}/invoices`);

export const adminInvoice = (invoiceId) => request(`/admin/invoices/${invoiceId}`);

export const adminStaff = () => request('/admin/staff');

export const adminAssignStaff = (userId, facilityId, isPrimary) =>
  request(`/admin/staff/${userId}/assignments`, { method: 'POST', body: { facility_id: facilityId, is_primary: isPrimary } });

export const adminUnassignStaff = (userId, facilityId) =>
  request(`/admin/staff/${userId}/assignments/${facilityId}`, { method: 'DELETE' });

export const adminSetStaffStatus = (userId, status) =>
  request(`/admin/staff/${userId}/status`, { method: 'PATCH', body: { status } });

// --- profile (self-service; every route is scoped to the caller) --------------
export const getProfile = () => request('/profile');

export const updateProfile = (body) => request('/profile', { method: 'PATCH', body });

export const updatePreferences = (preferences) =>
  request('/profile/preferences', { method: 'PATCH', body: preferences });

export const changePassword = (currentPassword, newPassword) =>
  request('/profile/password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  });

/**
 * Avatar upload does NOT go through request(): that sets Content-Type to
 * application/json, which would stop the browser generating the multipart
 * boundary the server needs to find the file part.
 */
export async function uploadAvatar(file) {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${BASE_URL}/profile/avatar`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error?.message ?? `Upload failed (${response.status})`);
    error.code = payload?.error?.code ?? 'UPLOAD_FAILED';
    error.status = response.status;
    throw error;
  }
  return payload.avatar;
}

export const deleteAvatar = () => request('/profile/avatar', { method: 'DELETE' });

/**
 * Facility photos. Same reason as uploadAvatar for bypassing request(): the
 * browser has to set the multipart boundary itself.
 */
export async function uploadFacilityPhoto(facilityId, file) {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${BASE_URL}/admin/facilities/${facilityId}/photos`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error?.message ?? `Upload failed (${response.status})`);
    error.code = payload?.error?.code ?? 'UPLOAD_FAILED';
    error.status = response.status;
    throw error;
  }
  return payload;
}

export const deleteFacilityPhoto = (facilityId, path) =>
  request(`/admin/facilities/${facilityId}/photos`, { method: 'DELETE', body: { path } });

export const reorderFacilityPhotos = (facilityId, order) =>
  request(`/admin/facilities/${facilityId}/photos/order`, { method: 'PATCH', body: { order } });


export const listMySessions = () => request('/profile/sessions');

export const revokeSession = (sessionId) =>
  request(`/profile/sessions/${sessionId}`, { method: 'DELETE' });

// --- system ----------------------------------------------------------------
export const health = () => request('/health');

export { request };

// --- platform ------------------------------------------------------------------
export const platformOverview = () => request('/platform/overview');

export const platformSettings = () => request('/platform/settings');

export const updatePlatformSettings = (body) =>
  request('/platform/settings', { method: 'PATCH', body });

export const platformInvoices = (limit) => request('/platform/invoices', { params: { limit } });

export const platformInvoice = (invoiceId) => request(`/platform/invoices/${invoiceId}`);

export const generatePlatformInvoice = (periodStart) =>
  request('/platform/invoices', { method: 'POST', body: { period_start: periodStart } });

export const markPlatformInvoicePaid = (invoiceId, note) =>
  request(`/platform/invoices/${invoiceId}/mark-paid`, { method: 'POST', body: { note } });
